import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { addDays, subDays } from "date-fns";

// Module-level cache for subscription status
let cachedStatus: SubscriptionStatus | null = null;
let lastChecked = 0;
const CACHE_TTL = 60000; // 60 seconds

// Type for response from the check-subscription Edge Function
interface SubscriptionCheckResponse {
  subscribed: boolean;
  isTrialing?: boolean; // New field added by Edge Function update
  trial_end_date: string | null;
  subscription_status: string | null;
  timestamp: string;
}

interface SubscriptionStatus {
  isLoading: boolean;
  isSubscribed: boolean;
  isTrialing: boolean;
  allowedDateRange: {
    startDate: Date;
    endDate: Date;
  } | null;
  error: string | null;
}

// For free users, limit calendar to 2 days before and after current date
const FREE_TIER_DATE_RANGE_DAYS = 2;

interface UseSubscriptionStatus {
  (session?: Session): {
    checkSubscriptionStatus: () => Promise<void>;
    isDateAllowed: (date: Date) => boolean;
    isLoading: boolean;
    isSubscribed: boolean;
    isTrialing: boolean;
    allowedDateRange: {
      startDate: Date;
      endDate: Date;
    } | null;
    error: string | null;
  };
  checkGlobalStatus?: (forceRefresh?: boolean) => Promise<SubscriptionStatus | null>;
}

export const useSubscriptionStatus: UseSubscriptionStatus = (session?: Session | null) => {
  // Check if query limits should be disabled (development mode)
  const disableQueryLimits = import.meta.env.VITE_DISABLE_QUERY_LIMITS === 'true';
  
  const [state, setState] = useState<SubscriptionStatus>({
    isLoading: true,
    // When query limits are disabled, treat everyone as subscribed
    isSubscribed: disableQueryLimits ? true : false,
    isTrialing: false,
    allowedDateRange: null,
    error: null,
  });

  const checkSubscriptionStatus = useCallback(async (userId: string) => {
    try {
      // Check cache first if we have a valid cached status
      const now = Date.now();
      if (cachedStatus && now - lastChecked < CACHE_TTL) {
        console.log('[useSubscriptionStatus] Using cached subscription status');
        setState(cachedStatus);
        return;
      }
      
      // Cache check handles concurrent requests
      
      setState(prev => ({ ...prev, isLoading: true }));
      
      // If query limits are disabled in development mode, mark as subscribed
      if (disableQueryLimits) {
        console.log('[useSubscriptionStatus] Development mode: All users treated as subscribed');
        const newState = {
          isLoading: false,
          isSubscribed: true,
          isTrialing: false,
          allowedDateRange: null,
          error: null,
        };
        cachedStatus = newState;
        lastChecked = now;
        setState(newState);
        return;
      }
      
      console.log('[useSubscriptionStatus] Calling check-subscription Edge Function');
      
      // Get the current auth session for the token - this ensures we have proper auth
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        console.error('[useSubscriptionStatus] No authentication token available');
        // Fallback to free tier for graceful degradation
        const fallbackState = {
          isLoading: false,
          isSubscribed: false,
          isTrialing: false,
          allowedDateRange: null,
          error: null,
        };
        cachedStatus = fallbackState;
        lastChecked = Date.now();
        setState(fallbackState);
        return;
      }
      
      // Call the Edge Function with explicit authentication
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-subscription`, 
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
        }
      );
      
      if (!response.ok) {
        console.error('[useSubscriptionStatus] Edge function returned error status:', response.status);
        
        // Handle error gracefully with fallback
        if (response.status >= 400) {
          console.error('[useSubscriptionStatus] Using fallback for error status code');
          
          // Fallback to free tier for graceful degradation
          const fallbackState = {
            isLoading: false,
            isSubscribed: false,
            isTrialing: false,
            allowedDateRange: null,
            error: null,
          };
          cachedStatus = fallbackState;
          lastChecked = Date.now();
          setState(fallbackState);
          return;
        }
        
        throw new Error(`Failed to check subscription: ${response.statusText}`);
      }
      
      const result: SubscriptionCheckResponse = await response.json();
      console.log('[useSubscriptionStatus] Edge Function response:', result);
      
      // First check for the explicit isTrialing flag, then fall back to status check
      // Use loose equality to handle type conversion since the boolean might come back as a string
      const isTrialing = result.isTrialing == true || result.subscription_status === 'trialing';
      console.log('[useSubscriptionStatus] Raw isTrialing value:', result.isTrialing, 'type:', typeof result.isTrialing);
      
      // A user can't be both subscribed and trialing - trial takes precedence
      const isSubscribed = !isTrialing && (result.subscribed === true || result.subscription_status === 'active');
      
      console.log('[useSubscriptionStatus] Interpreted status:', { isSubscribed, isTrialing, rawStatus: result.subscription_status });
      
      let allowedDateRange = null;
      
      // If user is not subscribed or in trial, set date range restrictions
      if (!isSubscribed && !isTrialing) {
        // Use UTC date for consistent comparison with the calendar
        const today = new Date();
        
        // Create UTC dates for range limits
        const startDate = new Date(Date.UTC(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - FREE_TIER_DATE_RANGE_DAYS
        ));
        
        const endDate = new Date(Date.UTC(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() + FREE_TIER_DATE_RANGE_DAYS
        ));
        
        allowedDateRange = {
          startDate,
          endDate,
        };
        
        console.log('[useSubscriptionStatus] Date range:', {
          today: today.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
      }
      
      // After successful check, save to cache before setting state
      const newState = {
        isLoading: false,
        isSubscribed,
        isTrialing,
        allowedDateRange,
        error: null,
      };
      cachedStatus = newState;
      lastChecked = Date.now();
      setState(newState);
    } catch (error) {
      console.error('[useSubscriptionStatus] Error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to check subscription status',
      }));
    }
  }, [disableQueryLimits]);
  
  // Create a globally shared check function that can be called from anywhere
  useSubscriptionStatus.checkGlobalStatus = async (forceRefresh = false) => {
    if (forceRefresh || !cachedStatus || Date.now() - lastChecked >= CACHE_TTL) {
      if (cachedStatus) {
        // If we have a session available, use that user ID
        const { data } = await supabase.auth.getSession();
        if (data?.session?.user?.id) {
          await checkSubscriptionStatus(data.session.user.id);
          return cachedStatus;
        }
      }
    }
    return cachedStatus;
  }

  // Function to check if a date is within the allowed range
  const isDateAllowed = (date: Date): boolean => {
    // In development mode with disabled query limits, all dates are allowed
    if (disableQueryLimits) {
      return true;
    }
    
    if (state.isSubscribed || state.isTrialing) {
      return true; // Subscribed users can view all dates
    }
    
    if (!state.allowedDateRange) {
      return true; // Default to allowed if range hasn't been calculated yet
    }
    
    const { startDate, endDate } = state.allowedDateRange;
    return date >= startDate && date <= endDate;
  };

  // Initial check when the hook is mounted
  useEffect(() => {
    if (session?.user?.id) {
      // Use a more aggressive check on mount/session change
      const checkStatus = async () => {
        console.log('[useSubscriptionStatus] Checking subscription on mount');
        await checkSubscriptionStatus(session.user.id);
      };
      
      checkStatus();
      
      // Periodic check every 5 minutes instead of 60 seconds to reduce noise
      const intervalId = setInterval(() => {
        if (session?.user?.id) {
          console.log('[useSubscriptionStatus] Periodic subscription check');
          checkSubscriptionStatus(session.user.id);
        }
      }, 300000); // Check every 5 minutes (300 seconds) - reduced from 60 seconds
      
      return () => clearInterval(intervalId);
    }
  }, [session?.user?.id, checkSubscriptionStatus]);

  return {
    ...state,
    checkSubscriptionStatus: session?.user ? () => checkSubscriptionStatus(session.user.id) : () => null,
    isDateAllowed,
  };
} 